import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { db } from '../../../firebase-client';
import {
  formatDiscountRedemptionLabel,
  formatDiscountRedemptionSource,
  resolveDiscountRedemptionMemberName,
} from '../../../utils/membershipDiscountRedemptions';
import EditButton from '../../ui/EditButton';

function formatTimestamp(value) {
  const date = typeof value?.toDate === 'function' ? value.toDate() : (value instanceof Date ? value : null);
  return date ? date.toLocaleString('en-GB') : '';
}

export default function DiscountsCard({ teamId, onEdit }) {
  const [rules, setRules] = useState([]);
  const [recentRedemptions, setRecentRedemptions] = useState([]);
  const [redemptionUsersById, setRedemptionUsersById] = useState({});
  const [redemptionsModalOpen, setRedemptionsModalOpen] = useState(false);

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    (async () => {
      const [rulesSnap, redemptionsSnap] = await Promise.all([
        getDocs(collection(db, `teams/${teamId}/membershipDiscounts`)).catch(() => ({ docs: [] })),
        getDocs(query(
          collection(db, `teams/${teamId}/membershipDiscountRedemptions`),
          orderBy('appliedAt', 'desc'),
          limit(25),
        )).catch(() => ({ docs: [] })),
      ]);
      if (cancelled) return;
      setRules((rulesSnap.docs || []).map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) })));
      setRecentRedemptions((redemptionsSnap.docs || []).map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) })));
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  useEffect(() => {
    const unresolvedUserIds = Array.from(new Set(
      recentRedemptions
        .map((entry) => (typeof entry?.userId === 'string' ? entry.userId.trim() : ''))
        .filter(Boolean)
    ));
    if (unresolvedUserIds.length === 0) {
      setRedemptionUsersById({});
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const loadedEntries = await Promise.all(
        unresolvedUserIds.map(async (uid) => {
          const snap = await getDoc(doc(db, 'users', uid)).catch(() => null);
          return [uid, snap?.exists() ? (snap.data() || {}) : null];
        })
      );
      if (cancelled) return;
      setRedemptionUsersById(Object.fromEntries(loadedEntries));
    })();

    return () => {
      cancelled = true;
    };
  }, [recentRedemptions]);

  const summary = useMemo(() => {
    const activeRules = rules.filter((rule) => String(rule.status || '').trim().toLowerCase() !== 'inactive');
    const totalUses = rules.reduce((total, rule) => total + Number(rule.usageCount || 0), 0);
    return {
      activeCount: activeRules.length,
      totalCount: rules.length,
      totalUses,
    };
  }, [rules]);
  const recentUsagePreview = recentRedemptions.slice(0, 3);

  return (
    <>
      <div className="team-card">
        <div className="card-header-with-icon">
          <span className="card-title">Membership Discounts</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="edit-btn"
              type="button"
              onClick={() => setRedemptionsModalOpen(true)}
              aria-label="View discount redemptions"
              title="View discount redemptions"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 2a8 8 0 105.293 14.293l5.207 5.207a1 1 0 001.414-1.414l-5.207-5.207A8 8 0 0010 2zm0 2a6 6 0 110 12 6 6 0 010-12z" />
              </svg>
            </button>
            <EditButton section="discounts" onEdit={onEdit} />
          </div>
        </div>

        <div className="card-body">
          {rules.length === 0 ? (
            <p className="text-muted">No membership discounts configured.</p>
          ) : (
            <>
              <p className="text-sm text-muted"><strong>Active rules:</strong> {summary.activeCount}/{summary.totalCount}</p>
              <p className="text-sm text-muted"><strong>Total redemptions:</strong> {summary.totalUses}</p>
            </>
          )}

          <div style={{ marginTop: '0.85rem' }}>
            <strong style={{ display: 'block', marginBottom: '0.35rem' }}>Recent usage</strong>
            {recentUsagePreview.length === 0 ? (
              <p className="text-muted">No redemptions logged yet.</p>
            ) : (
              <div style={{ display: 'grid', gap: '0.35rem' }}>
                {recentUsagePreview.map((entry, index) => {
                  const discountLabel = formatDiscountRedemptionLabel(entry);
                  const sourceLabel = formatDiscountRedemptionSource(entry.sourceType);
                  return (
                    <div
                      key={entry.id}
                      style={{
                        paddingBottom: index === recentUsagePreview.length - 1 ? 0 : '0.35rem',
                        borderBottom: index === recentUsagePreview.length - 1 ? 'none' : '1px solid #e5e7eb',
                      }}
                    >
                      <div className="small" style={{ fontWeight: 600, color: '#0f172a', lineHeight: 1.25 }}>
                        {resolveDiscountRedemptionMemberName(entry, redemptionUsersById[entry.userId])}
                      </div>
                      <div className="text-muted small" style={{ lineHeight: 1.25 }}>
                        {discountLabel}
                        {entry.amountAppliedMinor ? ` • £${(Number(entry.amountAppliedMinor || 0) / 100).toFixed(2)}` : ''}
                        {sourceLabel ? ` • ${sourceLabel}` : ''}
                      </div>
                      <div className="text-muted small" style={{ lineHeight: 1.2 }}>
                        {formatTimestamp(entry.appliedAt)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {redemptionsModalOpen && (
        <div className="modal-backdrop" onClick={() => setRedemptionsModalOpen(false)}>
          <div
            className="modal-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="membership-discount-redemptions-title"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '42rem' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '1rem' }}>
              <h2 id="membership-discount-redemptions-title" className="modal-title" style={{ margin: 0 }}>
                Recent Discount Redemptions
              </h2>
              <button type="button" className="button button-secondary" onClick={() => setRedemptionsModalOpen(false)}>
                Close
              </button>
            </div>

            <div className="modal-form">
              {recentRedemptions.length === 0 ? (
                <p className="text-muted">No discount redemptions recorded yet.</p>
              ) : (
                <div style={{ display: 'grid', gap: '0.4rem' }}>
                  {recentRedemptions.map((entry, index) => {
                    const discountLabel = formatDiscountRedemptionLabel(entry, { includeCode: false });
                    const sourceLabel = formatDiscountRedemptionSource(entry.sourceType);
                    return (
                      <div
                        key={entry.id}
                        style={{
                          paddingBottom: index === recentRedemptions.length - 1 ? 0 : '0.5rem',
                          borderBottom: index === recentRedemptions.length - 1 ? 'none' : '1px solid #e5e7eb',
                        }}
                      >
                        <div style={{ fontWeight: 600, color: '#0f172a', lineHeight: 1.25 }}>
                          {resolveDiscountRedemptionMemberName(entry, redemptionUsersById[entry.userId])}
                        </div>
                        <div className="text-muted small" style={{ lineHeight: 1.25 }}>
                          {discountLabel}
                          {entry.code ? ` • ${entry.code}` : ''}
                          {entry.amountAppliedMinor ? ` • £${(Number(entry.amountAppliedMinor || 0) / 100).toFixed(2)}` : ''}
                        </div>
                        <div className="text-muted small" style={{ lineHeight: 1.2 }}>
                          {formatTimestamp(entry.appliedAt)}
                          {sourceLabel ? ` • ${sourceLabel}` : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
